import clsx from "clsx";
import type { HealthStatus, OverallStatus } from "../types/api";
import { healthStyle, overallStyle } from "../lib/statusStyles";
import { relativeTime } from "../lib/relativeTime";
import { useNow } from "../hooks/useNow";

/**
 * Compact summary of a single problematic bridge for the banner's
 * "영향받는 브릿지" list. Parent (`StatusPage`) flattens sites+bridges and
 * keeps only DOWN/UNKNOWN entries to fill this.
 */
export interface AffectedBridge {
  lotId: string;
  lotName: string;
  siteDisplayName: string;
  lastUpdatedAt: string | null;
  status: HealthStatus;
}

interface OverallStatusBannerProps {
  /** Aggregate status from /api/status. Pass "UNKNOWN" while loading. */
  overall: OverallStatus;
  /** ISO-8601 string of when the response was generated. */
  generatedAt: string | null | undefined;
  /** When true, dim the banner slightly and append "최신 데이터 표시 중" hint. */
  staleHint?: boolean;
  /**
   * Currently failing bridges (DOWN/UNKNOWN), sorted by parent (usually
   * oldest-updated first). The banner renders up to the first 5 and a
   * "외 N개" overflow line. Only displayed for PARTIAL_OUTAGE / MAJOR_OUTAGE /
   * DEGRADED_PERFORMANCE — never on OPERATIONAL or UNKNOWN.
   */
  affected?: ReadonlyArray<AffectedBridge>;
}

const MAX_AFFECTED_PREVIEW = 5;

/**
 * Top-of-page status banner — status.claude.com signature element.
 *
 * Layout:
 *   ┌────────────────────────────────────────────────────────────┐
 *   │  ●  정상 동작                                                │
 *   │     모든 브릿지가 정상적으로 데이터를 전송하고 있습니다.       │
 *   │                                  generatedAt: 방금 전        │
 *   └────────────────────────────────────────────────────────────┘
 *
 * The color/label/description are all driven from `statusStyles.ts` so a new
 * enum value (or tweaked Korean copy) is a one-line edit.
 */
export default function OverallStatusBanner({
  overall,
  generatedAt,
  staleHint = false,
  affected,
}: OverallStatusBannerProps) {
  // 1-second tick so the "마지막 갱신 N초 전" label keeps moving even between
  // data refetches. Without this, the banner only refreshes when /api/status
  // returns a new response (every 30s) and sits frozen in between.
  const now = useNow();
  const style = overallStyle(overall);
  const generatedLabel = relativeTime(generatedAt, { referenceNow: now });

  // Only show the affected list for outage-flavored statuses. OPERATIONAL has
  // nothing to list and UNKNOWN means we don't have signal to assign blame.
  const showAffected =
    !!affected &&
    affected.length > 0 &&
    (overall === "PARTIAL_OUTAGE" ||
      overall === "MAJOR_OUTAGE" ||
      overall === "DEGRADED_PERFORMANCE");
  const previewAffected = showAffected
    ? affected.slice(0, MAX_AFFECTED_PREVIEW)
    : [];
  const overflowCount = showAffected
    ? Math.max(0, affected.length - MAX_AFFECTED_PREVIEW)
    : 0;

  return (
    <section
      role="status"
      aria-live="polite"
      className={clsx(
        "rounded-2xl border px-6 py-6 sm:px-8 sm:py-7 transition-colors",
        style.bg,
        style.border,
        staleHint && "opacity-90",
      )}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <span
            aria-hidden="true"
            className={clsx(
              "mt-1 inline-flex h-3 w-3 flex-shrink-0 rounded-full",
              style.solid,
              "ring-4",
              "ring-white/40 dark:ring-black/30",
            )}
          />
          <div>
            <h2
              className={clsx(
                "text-xl font-bold tracking-tight sm:text-2xl",
                style.fg,
              )}
            >
              {style.label}
            </h2>
            <p className={clsx("mt-1 text-sm sm:text-base", style.fg, "opacity-90")}>
              {style.description}
            </p>
          </div>
        </div>

        <div
          className={clsx(
            "flex flex-col items-start gap-0.5 text-xs sm:items-end",
            style.fg,
            "opacity-80",
          )}
        >
          <span className="font-medium uppercase tracking-wide">
            마지막 갱신
          </span>
          <span className="tabular-nums" title={generatedAt ?? undefined}>
            {generatedLabel}
          </span>
          {staleHint && (
            <span className="mt-1 italic">
              연결 문제로 최근 캐시 데이터를 표시 중
            </span>
          )}
        </div>
      </div>

      {showAffected && (
        <div className={clsx("mt-5 border-t pt-4", style.border, style.fg)}>
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide opacity-80">
              영향받는 브릿지
            </span>
            <span className="rounded-full bg-black/10 px-2 py-0.5 text-[11px] font-semibold tabular-nums dark:bg-white/10">
              {affected.length}
            </span>
          </div>
          <ul className="mt-2.5 grid gap-1.5 sm:grid-cols-2">
            {previewAffected.map((a) => (
              <li
                key={`${a.siteDisplayName}|${a.lotId}`}
                className="flex items-center gap-2.5 rounded-lg bg-white/45 px-3 py-2 dark:bg-black/20"
              >
                <span
                  aria-hidden="true"
                  className={clsx(
                    "h-2 w-2 flex-shrink-0 rounded-full",
                    healthStyle(a.status).solid,
                  )}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-1.5">
                    <span className="truncate text-sm font-semibold">
                      {a.lotName?.trim() || a.lotId}
                    </span>
                    <span className="flex-shrink-0 text-[11px] opacity-60">
                      {a.siteDisplayName}
                    </span>
                  </div>
                  <div
                    className="text-[11px] tabular-nums opacity-70"
                    title={a.lastUpdatedAt ?? undefined}
                  >
                    마지막 데이터{" "}
                    {relativeTime(a.lastUpdatedAt, { referenceNow: now })}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          {overflowCount > 0 && (
            <p className="mt-2 text-xs opacity-70">
              외 {overflowCount}개의 브릿지가 영향을 받고 있습니다
            </p>
          )}
        </div>
      )}
    </section>
  );
}
